/**
 * Filtro e página moram na URL, não no estado do componente.
 *
 * Três coisas passam a funcionar de graça: o botão "voltar", o F5 e o
 * "manda o link pra mim" — que é como a operação de fato trabalha.
 *
 * Só existe UM filtro, e é por estado. A busca textual saiu junto com os
 * campos que a API não tem: sem `protocolo` e sem `operadora`, não sobrou
 * nada legível por gente para casar com um termo digitado. Caixa de busca
 * que não busca nada é pior que caixa de busca nenhuma.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface Filtros {
  /** Estado no vocabulário do FIO (`Received`, …). `''` = todos. */
  readonly estado: string;
  /** 1-based, como a API. */
  readonly pagina: number;
}

export const FILTROS_VAZIOS: Filtros = { estado: '', pagina: 1 };

function lerPagina(bruto: string | null): number {
  const numero = Number(bruto);
  return Number.isInteger(numero) && numero >= 1 ? numero : 1;
}

export function useFiltrosNaUrl(): {
  filtros: Filtros;
  definirEstado: (valor: string) => void;
  definirPagina: (valor: number) => void;
  limparFiltros: () => void;
  temFiltroAtivo: boolean;
} {
  const [parametros, definirParametros] = useSearchParams();

  const filtros = useMemo<Filtros>(
    () => ({
      estado: parametros.get('estado') ?? '',
      pagina: lerPagina(parametros.get('pagina')),
    }),
    [parametros],
  );

  const escrever = useCallback(
    (mutacao: (proximos: URLSearchParams) => void) => {
      definirParametros(
        (atual) => {
          const proximos = new URLSearchParams(atual);
          mutacao(proximos);
          return proximos;
        },
        { replace: true },
      );
    },
    [definirParametros],
  );

  const definirEstado = useCallback(
    (valor: string) => {
      escrever((proximos) => {
        if (valor === '') proximos.delete('estado');
        else proximos.set('estado', valor);
        // Trocar de filtro volta para a primeira página: manter a página 3
        // mostraria vazio e a pessoa leria isso como "não tem nada".
        proximos.delete('pagina');
      });
    },
    [escrever],
  );

  const definirPagina = useCallback(
    (valor: number) => {
      escrever((proximos) => {
        if (valor <= 1) proximos.delete('pagina');
        else proximos.set('pagina', String(valor));
      });
    },
    [escrever],
  );

  const limparFiltros = useCallback(() => {
    definirParametros(new URLSearchParams(), { replace: true });
  }, [definirParametros]);

  return { filtros, definirEstado, definirPagina, limparFiltros, temFiltroAtivo: filtros.estado !== '' };
}
